import React, { CSSProperties, ChangeEvent, ReactNode } from 'react';
import { Table, Thead, Th, Tr, Td } from 'reactable';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import { Paginator } from './Paginator';
import { getTranslationKey } from '../modules/utils/helpers';

interface DefaultSorting {
  column: string;
  direction?: 'asc' | 'desc';
}

interface ServerSidePagination {
  onPageChange: (page: number) => void;
  currentPage: number;
  numPages: number;
}

interface DoubleRow {
  content: ReactNode;
}

interface SortableColumn {
  column: string;
  sortFunction?: () => void;
}

interface TableItemProps {
  items: any[];
  columns: string[];
  columnNames: string[];
  noMatchingRecords?: string | null;
  sortable?: string[] | SortableColumn[] | null;
  onSort?: ((sortInfo: { column: string; direction: string }) => void) | null;
  mappers: { [key: string]: (value: any, item: any) => any };
  doubleRows?: DoubleRow[] | null;
  defaultSorting?: DefaultSorting;
  itemsPerPage: number;
  serverSidePagination?: ServerSidePagination | null;
  search: string;
  searchableCols?: string[] | null;
  isLoading: boolean;
}

const TableItem: React.FC<TableItemProps> = ({
  items,
  columns,
  columnNames,
  noMatchingRecords,
  sortable,
  onSort,
  mappers,
  doubleRows,
  defaultSorting,
  itemsPerPage,
  serverSidePagination,
  search,
  searchableCols,
  isLoading
}) => (
  <Table
    className="table"
    itemsPerPage={serverSidePagination || itemsPerPage > items.length ? 0 : itemsPerPage}
    pageButtonLimit={5}
    onSort={onSort}
    sortable={sortable}
    defaultSort={defaultSorting}
    nextPageLabel={getTranslationKey('nextPageLabel')}
    previousPageLabel={getTranslationKey('previousPageLabel')}
    filterable={searchableCols || columns}
    hideFilterInput
    filterBy={search}
    noDataText={noMatchingRecords || getTranslationKey('noMatchingRecords')}
    style={
      {
        opacity: isLoading ? '0.5' : ''
      } as CSSProperties
    }
  >
    <Thead>
      {columns.map((column, i) => (
        <Th key={i} column={column}>
          {columnNames[i]}
        </Th>
      ))}
    </Thead>
    {doubleRows && doubleRows.map((item) => item.content)}

    {!doubleRows &&
      items.map((item, i) => (
        <Tr key={i}>
          {columns.map((column, j) => {
            const data = mappers[column] ? mappers[column](item[column], item) : item[column];

            return (
              <Td key={j} column={column} data={data}>
                <span>{data}</span>
              </Td>
            );
          })}
        </Tr>
      ))}
  </Table>
);

interface DefaultTableProps {
  items?: any[];
  columns?: string[];
  columnNames?: string[];
  itemsPerPage?: number;
  search?: string;
  onSearch?: ((event: ChangeEvent<HTMLInputElement>) => void) | null;
  searchPlaceholder?: string;
  searchableCols?: string[] | null;
  onChangeItemsPerPage?: ((event: ChangeEvent<HTMLSelectElement>) => void) | null;
  button?: ReactNode | null;
  noMatchingRecords?: string | null;
  sortable?: SortableColumn[] | null;
  onSort?: ((sortInfo: { column: string; direction: string }) => void) | null;
  serverSidePagination?: ServerSidePagination | null;
  footerActions?: ReactNode | null;
  mappers?: { [key: string]: (value: any, item: any) => any };
  tableStyles?: CSSProperties;
  bottomInput?: ReactNode | null;
  doubleRows?: DoubleRow[] | null;
  isLoading?: boolean;
  defaultSorting?: DefaultSorting;
  text?: string;
  className?: string;
}

const DefaultTable: React.FC<DefaultTableProps> = ({
  items = [],
  columns = [],
  columnNames = [],
  itemsPerPage = 10,
  search = '',
  onSearch = null,
  searchPlaceholder = '',
  searchableCols = null,
  onChangeItemsPerPage = null,
  button = null,
  noMatchingRecords = null,
  sortable = null,
  onSort = null,
  serverSidePagination = null,
  footerActions = null,
  mappers = {},
  tableStyles = {},
  bottomInput = null,
  doubleRows = null,
  isLoading = false,
  defaultSorting,
  className
}) => (
  <div className={`table-wrapper${className ? ` ${className}` : ''}`} style={tableStyles}>
    <div className="row d-flex">
      <div className="col-sm-12">{button}</div>
      {onSearch && (
        <div className="col-sm-12 col-md-4 mb-2">
          <InputGroup className="input-prepend pull-right">
            <InputGroupText>
              <i className="fa fa-search" />
            </InputGroupText>
            <Input
              value={search}
              onChange={onSearch}
              id="prependedInput"
              type="text"
              placeholder={searchPlaceholder || getTranslationKey('search')}
              spellCheck={false}
            />
          </InputGroup>
        </div>
      )}
    </div>
    {bottomInput && (
      <div className="row mb-3">
        <div className="col-sm-12 col-md-4 mb-2">{bottomInput}</div>
      </div>
    )}
    <div className="row" style={{ flexGrow: 2 }}>
      <div className="col-md-12">
        <div className="table-responsive">
          {isLoading && <div className="loader" />}
          {!isLoading && defaultSorting && (
            <TableItem
              items={items}
              columns={columns}
              columnNames={columnNames}
              noMatchingRecords={noMatchingRecords}
              sortable={sortable}
              onSort={onSort}
              search={search}
              searchableCols={searchableCols}
              mappers={mappers}
              doubleRows={doubleRows}
              defaultSorting={defaultSorting}
              serverSidePagination={serverSidePagination}
              itemsPerPage={itemsPerPage}
              isLoading={isLoading}
            />
          )}
          {/* Second table needed to make 'defaultSorting' field work as expected */}
          {!isLoading && !defaultSorting && (
            <TableItem
              items={items}
              columns={columns}
              columnNames={columnNames}
              noMatchingRecords={noMatchingRecords}
              sortable={sortable}
              onSort={onSort}
              search={search}
              searchableCols={searchableCols}
              mappers={mappers}
              doubleRows={doubleRows}
              defaultSorting={{ column: '' }}
              serverSidePagination={serverSidePagination}
              itemsPerPage={itemsPerPage}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
    <div className="row">
      <div className="col-md-12">
        <div className="table-footer">
          {footerActions}
          {serverSidePagination && !isLoading && (
            <Paginator
              onPageChange={serverSidePagination.onPageChange}
              currentPage={serverSidePagination.currentPage}
              numPages={serverSidePagination.numPages}
              pageButtonLimit={5}
              nextPageLabel={getTranslationKey('nextPageLabel')}
              previousPageLabel={getTranslationKey('previousPageLabel')}
            />
          )}
          {onChangeItemsPerPage && !isLoading && (
            <div>
              <form onSubmit={(e) => e.preventDefault()} className="pull-right">
                <div className="form-group form-inline">
                  <span className="mr-2">{getTranslationKey('showRecordsPerPage')}</span>
                  <select
                    className="custom-select m-r-10"
                    onChange={onChangeItemsPerPage}
                    value={itemsPerPage}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default DefaultTable;
